import { http } from "./http";

export type CustomerMembershipCoverageScope = "all_services" | "selected_services";
export type CustomerMembershipScopeMode =
  | "single_establishment"
  | "all_owner_establishments"
  | "selected_establishments";

export type SubscriptionCommissionMode = "none" | "percent" | "fixed";
export type CustomerMembershipPaymentMode = "online" | "manual";

/** Quem pode atender o assinante. Ausente em respostas antigas do backend. */
export type CustomerMembershipProviderScope = "any_provider" | "selected_providers";

/** Como o profissional e remunerado pelo servico coberto pela assinatura. */
export type CustomerMembershipCoveredServiceCommissionMode =
  | "none"
  | "fixed"
  | "percent"
  | "pool_by_count"
  | "pool_by_duration"
  | "subscriber_share";

/**
 * Como o profissional é remunerado por **vender** a assinatura. Eixo separado
 * da remuneração por atendimento: paga uma vez, no primeiro pagamento.
 */
export type CustomerMembershipAdhesionCommissionMode = "none" | "fixed" | "percent";

export type CustomerMembershipPlanServiceRule = {
  serviceId: string;
  /** Serviço-base cuja cota é compartilhada por todos os membros do grupo. */
  quotaGroupServiceId?: string;
  monthlyLimit: number | null;
  /** Override em `fixed`. */
  commissionValueCents?: number | null;
  /** Override em `percent`. */
  commissionValuePercent?: number | null;
  /** Override em `pool_by_duration`; `null` usa a duracao do servico. */
  poolPoints?: number | null;
};

export type CustomerMembershipPlan = {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  monthlyPriceCents: number;
  productDiscountPercent?: number | null;
  commissionProviderId?: string | null;
  commissionProviderName?: string | null;
  intervalUnit?: "month" | "day";
  billingDay?: number | null;
  paymentMode?: CustomerMembershipPaymentMode;
  coverageScope: CustomerMembershipCoverageScope;
  scopeMode?: CustomerMembershipScopeMode;
  coveredEstablishmentIds?: string[];
  subscriptionCommissionMode?: SubscriptionCommissionMode;
  subscriptionCommissionValuePercent?: number | null;
  subscriptionCommissionValueCents?: number | null;
  providerScope?: CustomerMembershipProviderScope;
  coveredServiceCommissionMode?: CustomerMembershipCoveredServiceCommissionMode;
  coveredServiceCommissionValueCents?: number | null;
  coveredServiceCommissionValuePercent?: number | null;
  poolRevenuePercent?: number | null;
  /**
   * Percentual da mensalidade de cada assinante repartido entre quem o
   * atendeu (`subscriber_share`). Campo proprio: `poolRevenuePercent` fica
   * nulo nessa modalidade.
   */
  subscriberSharePercent?: number | null;
  adhesionCommissionMode?: CustomerMembershipAdhesionCommissionMode;
  adhesionCommissionValueCents?: number | null;
  /** De 1 a 100; zero não existe — "não pagar adesão" é o modo `none`. */
  adhesionCommissionValuePercent?: number | null;
  active?: boolean;
  publicVisible?: boolean;
  coveredWeekdays?: number[];
  coveredWeekdayLabels?: string[];
  serviceIds?: string[];
  serviceRules?: CustomerMembershipPlanServiceRule[];
  providers?: { id: string; name: string }[] | null;
  /**
   * `true` só quando o plano nunca foi usado — sem assinatura (mesmo
   * cancelada), comissão, receita ou pote. Backend antigo não envia o campo,
   * e a ausência vale como "não pode": nunca oferecemos exclusão no escuro.
   */
  deletable?: boolean;
  /** Assinaturas que já apontaram para o plano, em qualquer status. */
  subscriptionsCount?: number;
};

export type CustomerMembershipCoverageDetails = {
  covered: boolean;
  reason: string;
  requiresConfirmation?: boolean;
  scheduledWeekday?: number | null;
  scheduledWeekdayLabel?: string | null;
  coveredWeekdays?: number[];
  coveredWeekdayLabels?: string[];
  message?: string;
  exceededServices?: Array<{
    serviceId: string;
    monthlyLimit: number;
    usedCount: number;
  }>;
  requiredProviderId?: string | null;
  selectedProviderId?: string | null;
  providerScope?: CustomerMembershipProviderScope;
  /** Preenchido em `PROVIDER_NOT_INCLUDED`. */
  allowedProviderIds?: string[] | null;
};

export type CustomerMembershipUsageService = {
  serviceId: string;
  serviceName: string | null;
  monthlyLimit: number | null;
  usedCount: number;
  remainingCount: number | null;
  limitReached: boolean;
};

export type CustomerMembershipUsageSummary = {
  coverageScope: CustomerMembershipCoverageScope;
  periodStart: string | null;
  periodEnd: string | null;
  isUnlimited: boolean;
  services: CustomerMembershipUsageService[];
};

export type CustomerMembershipSubscriptionStatus =
  | "inactive"
  | "pending"
  | "active"
  | "past_due"
  | "unpaid"
  | "canceled"
  | "trial";

export type CustomerMembershipBillingStatus =
  | "processing"
  | "payment_failed"
  | "paid"
  | "renewal_failed"
  | "past_due"
  | "canceled";

export type CustomerMembershipSummary = {
  id: string;
  establishmentId: string;
  status: CustomerMembershipSubscriptionStatus;
  billingStatus?: CustomerMembershipBillingStatus;
  hasPaidCycle?: boolean;
  hasActiveAccess?: boolean;
  provider?: "pagarme" | "manual" | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  nextBillingAt?: string | null;
  pendingSince?: string | null;
  cancelAtPeriodEnd?: boolean;
  providerSubscriptionId?: string | null;
  providerLastEvent?: string | null;
  providerId?: string | null;
  providerUserId?: string | null;
  plan?: CustomerMembershipPlan | null;
  usageSummary?: CustomerMembershipUsageSummary | null;
  establishment?: {
    id: string;
    name: string;
    slug: string;
  } | null;
};

export type ManagedCustomerAppointment = {
  id: string;
  customerName: string;
  customerEmail: string;
  customerWhatsapp: string;
  date: string;
  time: string;
  status: string;
  expiresAt?: string | null;
  cancelReason?: string | null;
  notes: string | null;
  serviceName: string | null;
  services: Array<{ id: string; name: string; durationMinutes: number }>;
  establishmentName: string;
  establishmentSlug: string;
  providerId?: string | null;
  providerName: string | null;
  timezone: string;
  cancellationHours?: number;
  hoursUntilAppointment?: number;
  canCancel?: boolean;
  canReschedule?: boolean;
};

export type CustomerMembershipSourceContext = {
  sourceEstablishment?: { id: string; name: string | null } | null;
  canManageAtSource?: boolean;
};

export type CustomerSubscriberRow = CustomerMembershipSourceContext & {
  customerId?: string | null;
  userId?: string | null;
  fullName?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  imageUrl?: string | null;
  avatarUrl?: string | null;
  phone?: string | null;
  cpf?: string | null;
  status: CustomerMembershipSubscriptionStatus | "none" | "without_account";
  billingStatus?: CustomerMembershipBillingStatus;
  hasActiveAccess: boolean;
  subscriptionId?: string | null;
  provider?: "pagarme" | "manual" | null;
  providerId?: string | null;
  providerName?: string | null;
  plan?: {
    id: string;
    name: string;
    monthlyPriceCents: number | null;
    productDiscountPercent?: number | null;
    commissionProviderId?: string | null;
    commissionProviderName?: string | null;
    intervalUnit?: "month" | "day";
    /**
     * Só o modo de cada família de remuneração, nunca o valor. É o mínimo para
     * o aviso de "Registrar pagamento" dizer se aquele pagamento gera comissão
     * ou não — ver lib/membershipPaymentNotice.ts.
     */
    coveredServiceCommissionMode?: CustomerMembershipCoveredServiceCommissionMode;
    adhesionCommissionMode?: CustomerMembershipAdhesionCommissionMode;
    subscriptionCommissionMode?: SubscriptionCommissionMode;
    coverageScope: CustomerMembershipCoverageScope;
    scopeMode?: CustomerMembershipScopeMode;
    billingDay?: number | null;
    paymentMode?: CustomerMembershipPaymentMode;
    providerScope?: CustomerMembershipProviderScope;
    providers?: { id: string; name: string }[] | null;
  } | null;
  currentPeriodEnd?: string | null;
  nextBillingAt?: string | null;
  billingAnchorDay?: number | null;
  billingAnchorSource?: "entry" | "plan" | "override" | "legacy" | null;
  pendingAdjustment?: {
    id: string;
    additionalDays: number;
    adjustmentAmountCents: number;
    mode?: "waived" | "charged";
    oldDueAt?: string | null;
    newDueAt?: string | null;
  } | null;
  lastPaymentAt?: string | null;
  cancelAtPeriodEnd?: boolean;
  updatedAt?: string | null;
  lastAppointmentAt?: string | null;
};

export type ManualCustomerMembershipDueDatePreview = {
  subscriptionId: string;
  currentDueDate: string;
  protectedThroughDate: string;
  newDueDate: string;
  billingDay: number;
  additionalDays: number;
  monthlyAmountCents: number;
  adjustmentAmountCents: number;
  courtesyExpectedAmountCents: number;
  proratedExpectedAmountCents: number;
  nextRegularDueDate: string;
};

export type AdminCustomerMembershipStatusLookup = CustomerMembershipSourceContext & {
  usageSummary?: CustomerMembershipUsageSummary | null;
  ok: boolean;
  matched: boolean;
  customer: {
    id: string;
    fullName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  subscription: {
    id: string;
    status: CustomerMembershipSubscriptionStatus;
    billingStatus?: CustomerMembershipBillingStatus;
    provider?: "pagarme" | "manual" | null;
    providerId?: string | null;
    providerName?: string | null;
    hasActiveAccess: boolean;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    nextBillingAt?: string | null;
    pendingSince?: string | null;
    cancelAtPeriodEnd?: boolean;
  } | null;
  plan: {
    id: string;
    name: string;
    monthlyPriceCents: number | null;
    productDiscountPercent?: number | null;
    commissionProviderId?: string | null;
    commissionProviderName?: string | null;
    coverageScope: CustomerMembershipCoverageScope;
    scopeMode?: CustomerMembershipScopeMode;
    coveredEstablishmentIds?: string[];
    paymentMode?: CustomerMembershipPaymentMode;
    coveredWeekdays?: number[];
    coveredWeekdayLabels?: string[];
  } | null;
};

export type AdminCustomerMembershipCoveragePreview =
  AdminCustomerMembershipStatusLookup & {
    coverage: CustomerMembershipCoverageDetails | null;
  };

export type AdminCustomerMembershipRevenuePreview = {
  grossAmountCents: number;
  netAmountCents: number;
  totalFeeCents: number;
  percentageFeeCents: number;
  fixedFeeCents: number;
  gatewayTotalFeeCents: number;
  systemFeePercentage: number;
  systemFeeCents: number;
  cetPercentage: number;
  mdrPercentage: number | null;
  gatewayFeeCents: number;
  antifraudFeeCents: number;
  payoutDays: number;
  effectiveFeePercentage: number;
};

export function getPublicCustomerMembershipPlan(slug: string) {
  return http<{
    enabled: boolean;
    establishmentId: string;
    plan: CustomerMembershipPlan | null;
    plans?: CustomerMembershipPlan[];
    serviceIds: string[];
  }>(`/public/${slug}/customer-membership`);
}

/** Sinal genérico; não autentica nem informa cobertura ou dados da assinatura. */
export async function checkPublicCustomerMembershipNotice(
  slug: string,
  contact: { email: string; phone?: string }
): Promise<{ showNotice: boolean }> {
  const result = await http<unknown>(
    `/public/${encodeURIComponent(slug)}/customer-membership/notice`,
    { method: "POST", body: contact }
  );
  if (
    !result ||
    typeof result !== "object" ||
    !("showNotice" in result) ||
    typeof result.showNotice !== "boolean"
  ) {
    throw new Error("Não foi possível verificar uma possível assinatura.");
  }
  return { showNotice: result.showNotice };
}

/** Cobertura de um serviço na checagem pública (MBR-12). */
export type PublicMembershipServiceCoverage = {
  serviceId: string;
  coveredByMembership: boolean;
  membershipCoverageReason: string | null;
  /** Preço cheio do serviço. */
  amountCents: number;
};

/** Campos de cobertura devolvidos ao remarcar ou mover (MBR-21/MBR-22). */
export type RescheduleMembershipCoverageFields = {
  coveredByMembership?: boolean;
  membershipCoverageStatus?: "covered" | "outside_coverage" | null;
  membershipCoverageReason?: string | null;
  membershipRequiresPayment?: boolean;
  serviceCoverages?: Array<{
    serviceId: string;
    coveredByMembership: boolean;
    membershipCoverageReason: string | null;
  }>;
  /** A remarcação mudou a cobertura. Com `membershipRequiresPayment`, avisar. */
  coverageChanged?: boolean;
};

export function checkPublicCustomerMembershipCoverage(
  slug: string,
  payload: {
    serviceId?: string;
    serviceIds?: string[];
    providerId?: string | null;
    date?: string;
    time?: string;
    scheduledAt?: string;
  },
  token?: string
) {
  return http<{
    ok: boolean;
    authenticated: boolean;
    covered: boolean;
    /** Parte dos serviços coberta e parte fora. Ausente em backend antigo. */
    partial?: boolean;
    amountDueCents?: number;
    totalAmountCents?: number;
    serviceCoverages?: PublicMembershipServiceCoverage[];
    reason: string;
    message: string;
    coverage?: CustomerMembershipCoverageDetails | null;
    establishmentId: string;
    availablePlans: CustomerMembershipPlan[];
    migrationPlans: CustomerMembershipPlan[];
    plan: CustomerMembershipPlan | null;
    subscription?: {
      id: string;
      status: CustomerMembershipSubscriptionStatus;
      currentPeriodStart?: string | null;
      currentPeriodEnd?: string | null;
      cancelAtPeriodEnd?: boolean;
      planId?: string | null;
    } | null;
    usageSummary: CustomerMembershipUsageSummary | null;
    exceededServices?: Array<{
      serviceId: string;
      monthlyLimit: number;
      usedCount: number;
    }>;
  }>(`/public/${slug}/customer-membership/coverage`, {
    method: "POST",
    body: payload,
    token,
  });
}

export function getAdminCustomerMembershipPlan(token: string, establishmentId: string) {
  return http<{
    plan: (CustomerMembershipPlan & { active: boolean }) | null;
    plans?: Array<CustomerMembershipPlan & { active: boolean }>;
    serviceIds: string[];
  }>(`/establishments/${establishmentId}/customer-membership/plan`, { token });
}

export function listAdminCustomerMembershipPlans(token: string, establishmentId: string) {
  return http<{
    ok: boolean;
    plans: Array<CustomerMembershipPlan & { active: boolean }>;
    availableProviders: Array<{ id: string; name: string }>;
  }>(`/establishments/${establishmentId}/customer-membership/plans`, {
    token,
  });
}

/** Payload unico de criacao e edicao de plano. */
export type AdminCustomerMembershipPlanInput = {
  active?: boolean;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  monthlyPriceCents: number;
  productDiscountPercent?: number | null;
  commissionProviderId?: string | null;
  providerIds?: string[];
  providerScope?: CustomerMembershipProviderScope;
  intervalUnit?: "month" | "day";
  billingDay?: number | null;
  paymentMode?: CustomerMembershipPaymentMode;
  coverageScope: CustomerMembershipCoverageScope;
  scopeMode?: CustomerMembershipScopeMode;
  selectedEstablishmentIds?: string[];
  subscriptionCommissionMode?: SubscriptionCommissionMode;
  subscriptionCommissionValuePercent?: number | null;
  subscriptionCommissionValueCents?: number | null;
  coveredServiceCommissionMode?: CustomerMembershipCoveredServiceCommissionMode;
  coveredServiceCommissionValueCents?: number | null;
  coveredServiceCommissionValuePercent?: number | null;
  poolRevenuePercent?: number | null;
  /**
   * Percentual da mensalidade de cada assinante repartido entre quem o
   * atendeu (`subscriber_share`). Campo proprio: `poolRevenuePercent` fica
   * nulo nessa modalidade.
   */
  subscriberSharePercent?: number | null;
  adhesionCommissionMode?: CustomerMembershipAdhesionCommissionMode;
  adhesionCommissionValueCents?: number | null;
  /** De 1 a 100; zero não existe — "não pagar adesão" é o modo `none`. */
  adhesionCommissionValuePercent?: number | null;
  publicVisible?: boolean;
  coveredWeekdays?: number[];
  serviceIds?: string[];
  serviceRules?: CustomerMembershipPlanServiceRule[];
  targetMode?: "current" | "all_owner_establishments" | "selected_establishments";
};

export function createAdminCustomerMembershipPlan(
  token: string,
  establishmentId: string,
  payload: AdminCustomerMembershipPlanInput
) {
  return http<{
    ok: boolean;
    plan: CustomerMembershipPlan & { active: boolean };
    createdCount?: number;
    createdPlans?: Array<{
      establishmentId: string;
      plan: CustomerMembershipPlan & { active: boolean };
    }>;
  }>(`/establishments/${establishmentId}/customer-membership/plans`, {
    method: "POST",
    token,
    body: payload,
  });
}

export function updateAdminCustomerMembershipPlan(
  token: string,
  establishmentId: string,
  planId: string,
  payload: AdminCustomerMembershipPlanInput
) {
  return http<{
    ok: boolean;
    plan: CustomerMembershipPlan & { active: boolean };
    updatedCount?: number;
    targetMode?: "current" | "all_owner_establishments";
  }>(`/establishments/${establishmentId}/customer-membership/plans/${planId}`, {
    method: "PUT",
    token,
    body: payload,
  });
}

export function createManualCustomerMembershipSubscription(
  token: string,
  establishmentId: string,
  payload: {
    customerId: string;
    planId: string;
    /**
     * Quem vendeu a assinatura, para a comissão por venda. Opcional: ausente
     * grava nulo, porque contratação sem vendedor é caso normal. O backend
     * recusa (`SOLD_BY_PROVIDER_INVALID`) profissional que não é da unidade nem
     * está alocado ativo nela.
     */
    soldByProviderId?: string | null;
    /**
     * MBR-17: obrigatório quando a conta não tem WhatsApp. Sem ele o backend
     * responde 422 `CUSTOMER_WHATSAPP_REQUIRED` (`field: "customerWhatsapp"`).
     */
    customerWhatsapp?: string | null;
    billingDay?: number | null;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    adjustmentMode?: "waived" | "charged";
    note?: string;
    /**
     * D-13: com outra assinatura ativa o backend responde
     * `409 ACTIVE_SUBSCRIPTION_EXISTS`. Só depois da confirmação explícita da
     * pessoa a tela repete o pedido com `replaceExisting: true`.
     */
    replaceExisting?: boolean;
  }
) {
  return http<{
    ok: boolean;
    subscription: CustomerMembershipSummary & { customerId?: string | null };
  }>(`/establishments/${establishmentId}/customer-membership/manual-subscriptions`, {
    method: "POST",
    token,
    body: payload,
  });
}

export function recordManualCustomerMembershipPayment(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  payload?: {
    expectedTotalAmountCents?: number;
    expectedCurrentDueDate?: string;
    idempotencyKey?: string;
    note?: string;
  }
) {
  return http<{
    ok: boolean;
    subscriptionId: string;
    status: CustomerMembershipSubscriptionStatus;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    nextBillingAt?: string;
    payment?: {
      id: string;
      periodStart: string;
      periodEnd: string;
      monthlyAmountCents: number;
      adjustmentAmountCents: number;
      totalAmountCents: number;
      paidAt: string;
    };
    idempotent?: boolean;
    commissionPool?: {
      poolId: string;
      commissionAmountCents: number;
      allocationsCount: number;
    } | null;
  }>(
    `/establishments/${establishmentId}/customer-membership/subscriptions/${subscriptionId}/record-payment`,
    {
      method: "POST",
      token,
      body: payload || {},
    }
  );
}

export function previewManualCustomerMembershipDueDate(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  billingDay: number
) {
  return http<ManualCustomerMembershipDueDatePreview>(
    `/establishments/${establishmentId}/customer-membership/manual-subscriptions/${subscriptionId}/due-date-preview`,
    {
      method: "POST",
      token,
      body: { billingDay },
    }
  );
}

export function changeManualCustomerMembershipDueDate(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  payload: {
    billingDay: number;
    adjustmentMode: "waived" | "charged";
    expectedCurrentDueDate: string;
    note?: string;
  }
) {
  return http<
    ManualCustomerMembershipDueDatePreview & {
      ok: boolean;
      adjustmentId: string;
      adjustmentStatus: "pending" | "applied";
      expectedAmountCents: number;
    }
  >(
    `/establishments/${establishmentId}/customer-membership/manual-subscriptions/${subscriptionId}/due-date`,
    {
      method: "PATCH",
      token,
      body: payload,
    }
  );
}

export function changeManualCustomerMembershipProvider(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  payload: { providerId: string; note?: string }
) {
  return http<{
    ok: boolean;
    subscriptionId: string;
    provider: { id: string; name: string };
  }>(
    `/establishments/${establishmentId}/customer-membership/manual-subscriptions/${subscriptionId}/provider`,
    {
      method: "PATCH",
      token,
      body: payload,
    }
  );
}

export function reactivateManualCustomerMembershipSubscription(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  payload?: { note?: string }
) {
  return http<{ ok: boolean; subscriptionId: string; status: string }>(
    `/establishments/${establishmentId}/customer-membership/subscriptions/${subscriptionId}/reactivate`,
    {
      method: "POST",
      token,
      body: payload || {},
    }
  );
}

export function cancelManualCustomerMembershipSubscription(
  token: string,
  establishmentId: string,
  subscriptionId: string,
  payload?: { note?: string }
) {
  return http<{ ok: boolean; subscriptionId: string; status: string }>(
    `/establishments/${establishmentId}/customer-membership/subscriptions/${subscriptionId}/cancel`,
    {
      method: "POST",
      token,
      body: payload || {},
    }
  );
}

export type CustomerMembershipPlanPoolStatus =
  | "preview"
  | "processing"
  | "closed"
  | "failed";

export type CustomerMembershipPlanPoolParticipant = {
  providerId: string;
  providerName: string | null;
  establishmentId: string | null;
  establishmentName: string | null;
  appointmentsCount: number;
  points: number;
  /** Participacao no pote x 10000. */
  shareBasisPoints: number;
  amountCents: number;
};

export type CustomerMembershipPlanPoolView = {
  period: string;
  status: CustomerMembershipPlanPoolStatus;
  /** Modalidade do plano; fora de modo pote vem `none`, `fixed` ou `percent`. */
  mode: CustomerMembershipCoveredServiceCommissionMode;
  revenuePercent: number;
  revenueCents: number;
  poolCents: number;
  /** Soma de `participants[].points`. */
  totalPoints: number;
  closesAt: string | null;
  participants: CustomerMembershipPlanPoolParticipant[];
  cycleMonthly?: {
    status: CustomerMembershipPlanPoolStatus;
    distributedCents: number;
    retainedCents: number;
    cycles: Array<{
      cycleId: string;
      nominalStart: string;
      effectiveEnd: string | null;
      status: string;
      estimated: boolean;
      appointmentsCount: number;
      points: number;
      distributedCents: number;
      retainedCents: number;
      participants: CustomerMembershipPlanPoolParticipant[];
    }>;
  };
};

/** Sem `period` o backend responde o mes corrente no fuso da unidade. */
export function getAdminCustomerMembershipPlanPool(
  token: string,
  establishmentId: string,
  planId: string,
  period?: string
) {
  return http<CustomerMembershipPlanPoolView>(
    `/establishments/${establishmentId}/customer-membership/plans/${planId}/pool`,
    {
      token,
      query: period ? { period } : undefined,
    }
  );
}

export function disableAdminCustomerMembershipPlan(
  token: string,
  establishmentId: string,
  planId: string
) {
  return http<{ ok: boolean }>(
    `/establishments/${establishmentId}/customer-membership/plans/${planId}`,
    {
      method: "DELETE",
      token,
    }
  );
}

/**
 * Apaga o plano de vez. Rota separada da de desativar de propósito: o backend
 * recusa com `PLAN_IN_USE` (409) se o plano tiver qualquer vínculo, mesmo que
 * a listagem tenha dito que dava.
 */
export function deleteAdminCustomerMembershipPlan(
  token: string,
  establishmentId: string,
  planId: string
) {
  return http<{ ok: boolean }>(
    `/establishments/${establishmentId}/customer-membership/plans/${planId}/permanent`,
    {
      method: "DELETE",
      token,
    }
  );
}

export function listAdminCustomerMembershipSubscribers(
  token: string,
  establishmentId: string,
  status?:
    | "all"
    | "active"
    | "none"
    | "canceled"
    | "past_due"
    | "unpaid"
    | "pending"
    | "trial"
) {
  return http<{
    ok: boolean;
    statusFilter: string;
    subscribers: CustomerSubscriberRow[];
  }>(`/establishments/${establishmentId}/customer-membership/subscribers`, {
    token,
    query: { status: status || "all" },
  });
}

export function getAdminCustomerMembershipStatus(
  token: string,
  establishmentId: string,
  params: {
    email?: string;
    phone?: string;
    name?: string;
    providerId?: string | null;
  }
) {
  return http<AdminCustomerMembershipStatusLookup>(
    `/establishments/${establishmentId}/customer-membership/customer-status`,
    {
      token,
      query: {
        email: params.email || undefined,
        phone: params.phone || undefined,
        name: params.name || undefined,
        providerId: params.providerId || undefined,
      },
    }
  );
}

export function previewAdminCustomerMembershipCoverage(
  token: string,
  establishmentId: string,
  payload: {
    customerId?: string;
    email?: string;
    phone?: string;
    name?: string;
    serviceId?: string;
    serviceIds?: string[];
    providerId?: string | null;
    date: string;
    time: string;
  }
) {
  return http<AdminCustomerMembershipCoveragePreview>(
    `/establishments/${establishmentId}/customer-membership/coverage-preview`,
    {
      method: "POST",
      token,
      body: payload,
    }
  );
}

export function getAdminCustomerMembershipRevenuePreview(
  token: string,
  establishmentId: string,
  amountCents: number
) {
  return http<{
    ok: boolean;
    preview: AdminCustomerMembershipRevenuePreview;
  }>(`/establishments/${establishmentId}/customer-membership/revenue-preview`, {
    token,
    query: {
      amountCents: Math.max(0, Math.round(Number(amountCents || 0))),
    },
  });
}

export function getCustomerMemberships(token: string) {
  return http<{ ok: boolean; memberships: CustomerMembershipSummary[] }>(
    "/customer/memberships",
    {
      token,
    }
  );
}

export function getCustomerMembershipByEstablishment(
  token: string,
  establishmentId: string
) {
  return http<{
    ok: boolean;
    establishment: {
      id: string;
      name: string;
      slug: string;
    };
    plan: CustomerMembershipPlan | null;
    plans?: CustomerMembershipPlan[];
    subscription: CustomerMembershipSummary | null;
  }>(`/customer/memberships/${establishmentId}`, {
    token,
  });
}

export function subscribeCustomerMembership(
  token: string,
  establishmentId: string,
  payload: {
    planId: string;
    providerId?: string;
    cardToken?: string;
    payerPhone?: string;
    payerName?: string;
    payerCpf?: string;
    description?: string;
    replaceExisting?: boolean;
    billingAddress?: {
      zipCode?: string;
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      country?: string;
    };
    billing_address?: {
      line_1?: string;
      line_2?: string;
      zip_code?: string;
      city?: string;
      state?: string;
      country?: string;
    };
  }
) {
  return http<{
    ok: boolean;
    membership: CustomerMembershipSummary;
  }>(`/customer/memberships/${establishmentId}/subscribe`, {
    method: "POST",
    token,
    body: payload,
  });
}

export function cancelCustomerMembership(token: string, establishmentId: string) {
  return http<{
    ok: boolean;
    pendingWebhook?: boolean;
    message?: string;
    membership: CustomerMembershipSummary | null;
  }>(`/customer/memberships/${establishmentId}/cancel`, {
    method: "POST",
    token,
  });
}

export function getCustomerAppointments(token: string, establishmentId?: string) {
  return http<{
    ok: boolean;
    appointments: Array<{
      id: string;
      establishmentId: string;
      establishmentName: string;
      establishmentSlug: string;
      scheduledAt: string;
      status: string;
      expiresAt?: string | null;
      cancelReason?: string | null;
      customerName?: string | null;
      customerEmail?: string | null;
      customerWhatsapp?: string | null;
      serviceName?: string | null;
      providerName?: string | null;
      durationMinutes?: number | null;
      serviceImageUrl?: string | null;
      coveredByMembership?: boolean;
      managementToken?: string | null;
      managementUrl?: string | null;
      paymentResume?: {
        kind: "appointment";
        slug: string;
        establishmentId: string;
        establishmentName?: string;
        amountCents: number;
        payerEmail: string;
        passCardFeesToClient?: "SIM" | "NÃO" | null;
        bookingPayload: {
          id: string;
          appointmentId: string;
          expiresAt: string;
          serviceName?: string;
          phone?: string;
          whatsapp?: string;
        };
        hold: { appointmentId: string; expiresAt: string };
        createdAt?: string;
      } | null;
    }>;
  }>("/customer/appointments", {
    token,
    query: { establishmentId },
  });
}

export function getManagedCustomerAppointment(managementToken: string) {
  return http<ManagedCustomerAppointment>(`/appointments/manage/${managementToken}`);
}

export function rescheduleManagedCustomerAppointment(
  managementToken: string,
  payload: {
    date: string;
    time: string;
    reason?: string;
    reasonNote?: string;
  }
) {
  return http<{ ok?: boolean } & RescheduleMembershipCoverageFields>(
    `/appointments/manage/${managementToken}/reschedule`,
    {
      method: "POST",
      body: payload,
    }
  );
}

export function cancelManagedCustomerAppointment(
  managementToken: string,
  payload?: {
    reason?: string;
    reasonNote?: string;
  }
) {
  return http(`/appointments/manage/${managementToken}/cancel`, {
    method: "POST",
    body: payload || {},
  });
}
